/**
 * Card host of every extranet table: an optional title row (+ subtitle), an
 * optional `DataList.Toolbar` and the integrated `DataList` table, fed by a
 * declarative {@link DataListConfig} (stable column keys, French labels in
 * config), with the uniform loading / error / empty states.
 *
 * - `_layoutOverrides` (injected by `withComponentRules`) is merged into the
 *   base config with `applyLayoutOverrides` (columns rename / hide / show /
 *   reorder / tooltips, default sort, toolbar filters / search, title);
 * - the base config is published under `componentId`
 *   (`usePublishLayoutConfig`) — plus `publishedActions` when the table
 *   renders action buttons in its cells — so the admin editor lists every
 *   target; the title slot is always editable (DataList host), even with no
 *   base title;
 * - **inline mode** (`rowCount` given — rows in `config.dataSource.data`):
 *   the card owns the loading / error / « aucune donnée disponible » states;
 *   the toolbar only shows when there are rows (an API-empty list shows the
 *   empty text alone) and filters the loaded rows client-side;
 * - **endpoint mode** (`rowCount` omitted — `config.dataSource.endpoint`):
 *   the row count is unknown until DataList fetched, so DataList renders its
 *   own skeleton / error alert / empty message and the toolbar is always
 *   shown (it drives the request params);
 * - **server-filtered inline mode** (`onToolbarChange` given): the rows are
 *   inline, but loaded by the owner with the toolbar state as request params.
 *   The toolbar runs in its own DataList context (so it never filters the
 *   rows client-side) and reports a {@link ListToolbarQuery} — debounced,
 *   trimmed search + every filter value by stable id — on mount and on each
 *   change. It always shows (also while loading, on error or with no rows),
 *   and stays mounted while the owner refetches;
 * - `toolbarPlacement`: `'inline'` (default) — a band at the top of the table
 *   card; `'detached'` — its own card above the table card, as in the list
 *   mockups (fiches 22986–22989): the search grows to fill the row, the
 *   status select keeps its width on the right, both stack full-width below
 *   768px, and there is no reset (×) button (the select offers « Tous … »);
 * - below 768px `DataList` renders its own mobile card layout — that is how
 *   the design's mobile cards are met (nothing hand-rolled).
 *
 * Title policy of the module: the page header carries the screen title and
 * subtitle, so list and tab cards pass **no** `title` — a `setTitle` rule
 * adds a title row (in the table card).
 *
 * Shared host of `ClaimsTableCard` / `GuaranteesTableCard`, the tab tables and
 * the list cards (contracts, claims, premiums, fleet). Not registered by
 * itself.
 */

import { useEffect, useMemo, useRef, type ReactNode } from 'react';
import type { LucideIcon } from 'lucide-react';

import { useDebouncedValue } from '@hooks/useDebouncedValue';
import { Text } from '@components/base/display';
import { Card, VStack } from '@components/base/layout';
import {
  DataList,
  useDataList,
  type DataListConfig,
  type ToolbarConfig,
} from '@components/integrated/data-list';
import type { ActionsConfig } from '@components/integrated/entity-form';
import {
  applyLayoutOverrides,
  buildPublishedConfigFromDataList,
  resolveCardTitle,
  usePublishLayoutConfig,
  type LayoutOverrides,
} from '@components/shared/configurable';

import { CardBodyState } from './CardBodyState';
import { CardTitleRow } from './CardTitleRow';
import {
  LIST_SEARCH_DEBOUNCE_MS,
  toolbarFilterValues,
  type ListToolbarQuery,
} from './listToolbarConfig';

/** Where `DataTableCard` renders its toolbar. */
export type ToolbarPlacement = 'inline' | 'detached';

export interface DataTableCardProps {
  /** Registered id of the usage (publish target). */
  componentId: string;
  /** Base title (overridable); omitted ⇒ no header unless a rule sets one. */
  title?: string;
  /** Muted line under the title (shown with the header). */
  subtitle?: string;
  icon?: LucideIcon;
  /** Base (pre-override) config — inline rows or an endpoint. */
  config: DataListConfig;
  /**
   * Number of inline rows (empty state + count pill). Omit it in endpoint
   * mode: DataList then renders its own loading / error / empty states.
   */
  rowCount?: number;
  loading?: boolean;
  error?: string | null;
  /** Show the row count next to the title (inline mode). Default `false`. */
  showCount?: boolean;
  /** Right-hand slot of the title row. */
  headerTrailing?: ReactNode;
  /** Render `DataList.Toolbar` (search + filters of `config.toolbar`). */
  toolbar?: boolean;
  /** `'inline'` (default): band atop the table card; `'detached'`: own card above it. */
  toolbarPlacement?: ToolbarPlacement;
  /**
   * Server-side filtering of inline rows: receives the toolbar state (on
   * mount and on every change) instead of filtering the rows client-side.
   */
  onToolbarChange?: (query: ListToolbarQuery) => void;
  /** Action buttons rendered in the cells, published with the base config. */
  publishedActions?: ActionsConfig;
  /** Injected by `withComponentRules` — never passed by hand. */
  _layoutOverrides?: LayoutOverrides;
}

const NO_ROWS: Record<string, unknown>[] = [];
const NO_COLUMNS: DataListConfig['columns'] = {};

/** Classes of the inline toolbar band. */
const INLINE_TOOLBAR_CLASSES = 'border-b border-secondary-200 px-4 py-3';

/**
 * Report the toolbar state to the owner: the search debounced (by
 * `search.debounceMs`) and trimmed, the filters by stable id — once on mount,
 * then whenever the reported value actually changes.
 */
function useReportToolbarQuery(
  toolbar: ToolbarConfig | undefined,
  searchTerm: string,
  filterValues: Readonly<Record<string, unknown>>,
  onChange: (query: ListToolbarQuery) => void,
): void {
  const search = useDebouncedValue(
    searchTerm.trim(),
    toolbar?.search?.debounceMs ?? LIST_SEARCH_DEBOUNCE_MS,
  );
  const filters = useMemo(
    () => toolbarFilterValues(toolbar?.filters, filterValues),
    [toolbar?.filters, filterValues],
  );

  const onChangeRef = useRef(onChange);
  useEffect(() => {
    onChangeRef.current = onChange;
  });

  const reportedRef = useRef<string | null>(null);
  useEffect(() => {
    const signature = JSON.stringify([search, filters]);
    if (signature === reportedRef.current) return;
    reportedRef.current = signature;
    onChangeRef.current({ search, filters });
  }, [search, filters]);
}

interface ReportingToolbarProps {
  /** The override-merged list config (its `toolbar` drives the inputs). */
  config: DataListConfig;
  onChange: (query: ListToolbarQuery) => void;
  stretch: boolean;
}

/**
 * The toolbar of the server-filtered inline mode: `DataList.Toolbar` in a
 * DataList context of its own (no rows, no columns — controlled through
 * `useDataList`), whose state is reported instead of filtering any row.
 */
function ReportingToolbar({ config, onChange, stretch }: Readonly<ReportingToolbarProps>) {
  const toolbarConfig = useMemo<DataListConfig>(
    () => ({
      id: `${config.id}--toolbar`,
      dataSource: { data: NO_ROWS },
      columns: NO_COLUMNS,
      toolbar: config.toolbar,
      i18n: config.i18n,
      cssOverrides: config.cssOverrides,
    }),
    [config.id, config.toolbar, config.i18n, config.cssOverrides],
  );
  const list = useDataList(toolbarConfig);
  useReportToolbarQuery(toolbarConfig.toolbar, list.searchTerm, list.filterValues, onChange);

  return (
    <DataList config={toolbarConfig} list={list} className="!gap-0">
      <DataList.Toolbar stretch={stretch} showReset={!stretch} />
    </DataList>
  );
}

export function DataTableCard({
  componentId,
  title,
  subtitle,
  icon,
  config: baseConfig,
  rowCount,
  loading = false,
  error = null,
  showCount = false,
  headerTrailing,
  toolbar = false,
  toolbarPlacement = 'inline',
  onToolbarChange,
  publishedActions,
  _layoutOverrides,
}: Readonly<DataTableCardProps>) {
  usePublishLayoutConfig(componentId, {
    ...buildPublishedConfigFromDataList(baseConfig, title),
    ...(publishedActions ? { actions: publishedActions } : {}),
  });

  const config = useMemo(
    () => applyLayoutOverrides(baseConfig, _layoutOverrides),
    [baseConfig, _layoutOverrides],
  );
  const reportsToolbar = onToolbarChange !== undefined;
  // Server-filtered mode: the table itself never filters (no toolbar config).
  const tableConfig = useMemo<DataListConfig>(
    () => (reportsToolbar ? { ...config, toolbar: undefined } : config),
    [config, reportsToolbar],
  );

  const resolvedTitle = resolveCardTitle(title ?? '', _layoutOverrides);
  const hasSubtitle = subtitle !== undefined && subtitle !== '';
  const count = showCount && rowCount !== undefined && !loading && !error ? rowCount : undefined;
  const detached = toolbarPlacement === 'detached';
  const empty = rowCount === 0;
  // Client-filtering / endpoint modes: the toolbar shows with the table body.
  const bodyShown = !loading && !error && !empty;

  const titleRow = (
    <CardTitleRow title={resolvedTitle} icon={icon} count={count} trailing={headerTrailing} />
  );
  const header = (resolvedTitle !== '' || hasSubtitle) && (
    <Card.Header>
      {hasSubtitle ? (
        <VStack gap={1} className="min-w-0 flex-1">
          {resolvedTitle !== '' && titleRow}
          <Text size="sm" color="muted" data-testid={`data-table-subtitle-${componentId}`}>
            {subtitle}
          </Text>
        </VStack>
      ) : (
        titleRow
      )}
    </Card.Header>
  );

  /** The detached toolbar card (search + select above the table card). */
  const toolbarCard = (content: ReactNode) => (
    <Card padding="md" data-testid={`data-table-toolbar-${componentId}`}>
      <Card.Content>{content}</Card.Content>
    </Card>
  );

  // -- Server-filtered inline mode: toolbar and table in separate contexts --
  if (onToolbarChange !== undefined) {
    const reportingToolbar = toolbar ? (
      <ReportingToolbar config={config} onChange={onToolbarChange} stretch={detached} />
    ) : null;
    const tableBody = (
      <CardBodyState loading={loading} error={error} empty={empty}>
        <div data-testid={`data-table-${componentId}`}>
          <DataList config={tableConfig} className="!gap-0">
            <DataList.Table />
          </DataList>
        </div>
      </CardBodyState>
    );

    if (detached) {
      return (
        <VStack gap={4}>
          {reportingToolbar !== null && toolbarCard(reportingToolbar)}
          <Card padding="none">
            {header}
            <Card.Content>{tableBody}</Card.Content>
          </Card>
        </VStack>
      );
    }
    return (
      <Card padding="none">
        {header}
        {reportingToolbar !== null && (
          <div className={INLINE_TOOLBAR_CLASSES}>{reportingToolbar}</div>
        )}
        <Card.Content>{tableBody}</Card.Content>
      </Card>
    );
  }

  // -- Detached toolbar sharing the table's DataList context ---------------
  if (detached) {
    return (
      <DataList config={config} className="!gap-4">
        {toolbar && bodyShown && toolbarCard(<DataList.Toolbar stretch showReset={false} />)}
        <Card padding="none">
          {header}
          <Card.Content>
            <CardBodyState loading={loading} error={error} empty={empty}>
              <div data-testid={`data-table-${componentId}`}>
                <DataList.Table />
              </div>
            </CardBodyState>
          </Card.Content>
        </Card>
      </DataList>
    );
  }

  // -- Inline toolbar (default) ---------------------------------------------
  return (
    <Card padding="none">
      {header}
      <Card.Content>
        <CardBodyState loading={loading} error={error} empty={empty}>
          <div data-testid={`data-table-${componentId}`}>
            <DataList config={config} className="!gap-0">
              {toolbar && (
                <div className={INLINE_TOOLBAR_CLASSES}>
                  <DataList.Toolbar />
                </div>
              )}
              <DataList.Table />
            </DataList>
          </div>
        </CardBodyState>
      </Card.Content>
    </Card>
  );
}
